import { useParams, Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const relatedArticles = article ? getRelatedArticles(article) : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">你访问的文章可能已被删除或不存在。</p>
            <Link
              to="/"
              className="bg-primary-500 text-background-50 px-6 py-2.5 rounded-full text-sm font-label whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '17c一起',
    },
  };

  return (
    <>
      <PageSEO
        title={article.title}
        description={article.excerpt}
        keywords={`17c一起, ${article.category}, ${article.tags.join(', ')}`}
        canonicalUrl={`https://17cyiqi.com/article/${article.id}`}
        ogType="article"
      />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <article className="pb-16">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="bg-secondary-500 text-background-50 text-xs px-3 py-1 rounded-full font-label whitespace-nowrap">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-background-100 text-foreground-500 text-xs px-2.5 py-1 rounded-full border border-background-200/70 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="font-label text-foreground-700">{article.author}</span>
                </div>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-time-line"></i> 阅读约{article.readTime}
                </span>
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-[300px] md:h-[450px] object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="text-lg font-heading text-foreground-950 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors group cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          title={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-xs font-label font-medium text-foreground-900 line-clamp-2 leading-snug">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-1">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}